import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon, type IconName } from '../../../../components';
import {
  Root,
  Trigger,
  Value,
  Chevron,
  Menu,
  MenuItem,
  ItemMeta,
  ItemIcon,
  SearchRow,
  SearchField,
  SearchInput,
  NoMatch,
  RangeForm,
  RangeFields,
  DateField,
  DateArrow,
  RangeFoot,
  RangeNote,
} from './IntervalDropdown.styles';

/** A selectable range/interval: `value` is the code stored in state, `label` the
 *  copy shown (e.g. value `7d` → "Last 7 days"). */
export type IntervalOption<T extends string> = {
  value: T;
  label: string;
  /** A quiet figure at the option's right (e.g. its visitors, or a placeholder while they load). */
  meta?: React.ReactNode;
  /** Listed under the option before it (one step in), e.g. a platform under its source. */
  nested?: boolean;
  /** The pill's text while this option is picked, when its own label needs its parent's ("Search ads › Google"). */
  selectedLabel?: string;
  /** A small picture before the label. */
  icon?: React.ReactNode;
  /** Listed only when a search finds it (a long tail that would crowd the list, e.g. every referring website). */
  searchOnly?: boolean;
};

export type IntervalDropdownProps<T extends string> = {
  value: T;
  options: readonly IntervalOption<T>[];
  onChange: (value: T) => void;
  /** The trigger's icon (default: the clock of a time range). */
  icon?: IconName;
  /** The trigger's accessible name, already translated (default: "Change time range"). */
  ariaLabel?: string;
  /** A search field above the options (both texts already translated); the options are matched as typed. */
  search?: OptionSearch<T>;
  /** A last row that picks any run of days: two dates (YYYY-MM-DD) and Apply. */
  custom?: CustomRangeChoice;
  /** The pick narrows what the page shows (an applied filter): the pill takes the applied-filter orange. */
  active?: boolean;
};

/** The custom range row's dates: the current one (null: a preset is picked), the last pickable day and the longest
 *  run of days allowed (and the shortest, one day unless given). */
export type CustomRangeChoice = {
  value: { from: string; to: string } | null;
  max: string;
  maxDays: number;
  minDays?: number;
  onApply: (from: string, to: string) => void;
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Days from `from` to `to`, both included (NaN when either is not a date). Pure. */
const daysBetween = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS) + 1;

/** A search over the options. `extra`: choices made from the search itself, listed after the matches (e.g. any
 *  web address typed, as a source to filter by). */
export type OptionSearch<T extends string> = {
  placeholder: string;
  empty: string;
  extra?: (search: string) => IntervalOption<T>[];
};

/** The options a search shows: the matches, then its extra choices. Pure. */
export const searchOptions = <T extends string>(
  options: readonly IntervalOption<T>[],
  search: OptionSearch<T> | undefined,
  query: string,
): IntervalOption<T>[] => {
  if (!search) return matchOptions(options, '');
  const matches = matchOptions(options, query);
  return query.trim() && search.extra ? [...matches, ...search.extra(query)] : matches;
};

/**
 * The options a search keeps, ignoring case: each match, with its source above it when it is a nested option, and
 * every nested option of a matching parent (a platform is never shown without its source). Without a search, the
 * search-only options stay out. Pure.
 */
export const matchOptions = <T extends string>(
  options: readonly IntervalOption<T>[],
  search: string,
): IntervalOption<T>[] => {
  const needle = search.trim().toLowerCase();
  if (!needle) return options.filter((o) => !o.searchOnly);
  const hit = (o: IntervalOption<T>): boolean => o.label.toLowerCase().includes(needle);
  const keep = new Set<number>();
  let parent = -1;
  options.forEach((o, i) => {
    if (!o.nested) parent = i;
    const parentHit = !!o.nested && parent >= 0 && hit(options[parent]);
    if (hit(o) || parentHit) {
      keep.add(i);
      if (o.nested && parent >= 0) keep.add(parent);
    }
  });
  return options.filter((_, i) => keep.has(i));
};

/**
 * Range/interval selector — a clock-icon pill trigger (the active option's label)
 * that opens a small listbox of options. Option-driven so the same control serves
 * any page. Option `label`s arrive already translated (the caller localizes them at
 * its render site), so they render as plain nodes; the trigger's `aria-label` goes
 * through `t()`. Closes on outside click.
 */
export const IntervalDropdown = <T extends string>({
  value,
  options,
  onChange,
  icon = 'clock',
  ariaLabel,
  search,
  custom,
  active = false,
}: IntervalDropdownProps<T>): React.ReactElement => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const searchRef = React.useRef<HTMLInputElement>(null);
  // The custom range's dates while they are being picked (from the current range, or ending on the last day).
  const [rangeOpen, setRangeOpen] = React.useState(false);
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');
  React.useEffect(() => {
    if (!open || !custom) return;
    setRangeOpen(custom.value !== null);
    setFrom(custom.value?.from ?? '');
    setTo(custom.value?.to ?? custom.max);
    // Seeded each time the menu opens, not as the caller re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const span = from && to ? daysBetween(from, to) : NaN;
  const minDays = custom?.minDays ?? 1;
  const rangeValid = custom !== undefined && span >= minDays && span <= custom.maxDays && to <= custom.max;
  // A search starts empty each time the menu opens, with the field ready to type in.
  React.useEffect(() => {
    if (!open) setQuery('');
    else searchRef.current?.focus();
  }, [open]);
  const shown = searchOptions(options, search, query);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent): void => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <Root ref={ref}>
      <Trigger
        $active={active}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel ?? t('Change time range')}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name={icon} size={16} />
        <Value>{selected?.selectedLabel ?? selected?.label ?? value}</Value>
        <Chevron $open={open} aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Chevron>
      </Trigger>
      {open ? (
        <Menu role="listbox" $wide={!!search || !!custom}>
          {search ? (
            <SearchRow>
              <SearchField>
                <Icon name="search" size={16} />
                <SearchInput
                  ref={searchRef}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={search.placeholder}
                  aria-label={search.placeholder}
                />
              </SearchField>
            </SearchRow>
          ) : null}
          {search && shown.length === 0 ? <NoMatch>{search.empty}</NoMatch> : null}
          {shown.map((o) => (
            <MenuItem
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === value}
              $active={o.value === value}
              $nested={!!o.nested}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              {o.icon !== undefined ? <ItemIcon aria-hidden="true">{o.icon}</ItemIcon> : null}
              {o.label}
              {o.meta !== undefined ? <ItemMeta>{o.meta}</ItemMeta> : null}
            </MenuItem>
          ))}
          {custom ? (
            <>
              <MenuItem
                type="button"
                $active={custom.value !== null}
                aria-expanded={rangeOpen}
                onClick={() => setRangeOpen((o) => !o)}
              >
                <ItemIcon aria-hidden="true">
                  <Icon name="calendar" size={16} />
                </ItemIcon>
                {t('Custom range')}
              </MenuItem>
              {rangeOpen ? (
                <RangeForm
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!rangeValid) return;
                    custom.onApply(from, to);
                    setOpen(false);
                  }}
                >
                  <RangeFields>
                    <DateField
                      type="date"
                      value={from}
                      max={to || custom.max}
                      aria-label={t('From date')}
                      onChange={(e) => setFrom(e.target.value)}
                    />
                    <DateArrow aria-hidden="true">&rarr;</DateArrow>
                    <DateField
                      type="date"
                      value={to}
                      min={from || undefined}
                      max={custom.max}
                      aria-label={t('To date')}
                      onChange={(e) => setTo(e.target.value)}
                    />
                  </RangeFields>
                  <RangeFoot>
                    <RangeNote $warn={from !== '' && to !== '' && !rangeValid}>
                      {minDays > 1
                        ? t('{{min}} to {{max}} days.', { min: minDays, max: custom.maxDays })
                        : t('Up to {{n}} days.', { n: custom.maxDays })}
                    </RangeNote>
                    <Button type="submit" variant="primary" label={t('Apply')} disabled={!rangeValid} />
                  </RangeFoot>
                </RangeForm>
              ) : null}
            </>
          ) : null}
        </Menu>
      ) : null}
    </Root>
  );
};

export default IntervalDropdown;
